import {useMemo, lazy, useState, useRef, Suspense, useLayoutEffect} from "react";
import {DragController, useDropZone} from "@codecks/dnd";
import {mergeRefs} from "react-merge-refs";
import Deck from "../../components/Deck";
import {api} from "../../lib/api";
import {XGrid, XRow, XCol} from "../../components/xui";
import FlipGroup from "react-flip-primitives";
import ArenaLayout from "../../layouts/arena-layout";
import {useOrderedSelectedProjects} from "../../lib/hooks/useSelectedProjects";
import xcolors from "../../components/xui/xcolors";
import OverviewActionButtons from "./OverviewActionButtons";
import {
  Arrow,
  RevealSpinner,
  Spinner,
  TooltipForChild,
  cx,
  errorToString,
  uiStyles,
} from "@cdx/common";
import DecksCreatePlaceholder from "./DeckCreatePlaceholder";
import {canDeckBeMoved} from "../../lib/cross-project-cards-or-decks";
import {HintForHintKey} from "../../components/Hint";
import {animated, useSpring} from "react-spring";
import {useFilteredCards} from "../../features/search/useSearch";
import WelcomeHints from "../../features/hints/WelcomeHints";
import messenger from "../../lib/messenger";
import {projectDecks} from "../../lib/utils";
import {deckOverviewStyles as styles} from "./decks.css";
import {getDecksArenaContext} from "./decksArenaContext";
import {ArenaContextProvider} from "../../layouts/arena-layout/createArenaContext";
import Search from "../../features/search/search";
import {hasPermissionToCreateDeck, hasPermissionToModifyDeck} from "../../lib/permissions";
import {
  Box,
  Col,
  DSIconButton,
  DSIconChevronDown,
  DSIconChevronUp,
  DSIconCog,
  DSIconGlobe,
  Row,
} from "@cdx/ds";
import {checkIfPresent} from "../../lib/mate/mate-utils";
import {
  CreateSpace,
  EditOrderButton,
  EditSpaceLabel,
  SpaceIcon,
  SpaceStats,
  getDeckOrderWithSpaces,
} from "./SpaceComponents";
import {useLocalStorageState} from "../../lib/storage";
import routes from "../../routes";
import dsStyles from "@cdx/ds/css/index.css";
import {waitForResultPromise} from "../../lib/wait-for-result";
import ProjectHeader from "./ProjectHeader";
import cdxEnv from "../../env";
import {alphaSeq} from "../../lib/sequences";
import {getModalUrl} from "../../components/ModalRegistry";

export const DECKWIDTHWOMARGIN = 165;
export const DECKWIDTH = DECKWIDTHWOMARGIN + 2 * 12;
export const DECKHEIGHT = 250 + 24;

const canDropFn = ({canModifyOrder}) => {
  if (!canModifyOrder) {
    return {
      canDrop: false,
      message: "You don't have permissions to change order of public decks",
    };
  }
  return {canDrop: true};
};

const DeckLane = ({
  decks,
  offset,
  registerFlipNode,
  selectedDeckId,
  isSomeDeckSelected,
  bag,
  lastEl,
}) => {
  const {onDeckDrop, handleDragOver, root, history, project, space, canModifyOrder} = bag;

  const {ref} = useDropZone({
    type: "DECK",
    onDragOver: (data) => handleDragOver(data, offset, decks),
    onDrop: onDeckDrop,
  });

  const refs = useMemo(
    () =>
      mergeRefs([
        ref,
        registerFlipNode(`batch-${offset}`, {onPresence: (val) => ({opacity: val})}),
      ]),
    [offset, ref, registerFlipNode]
  );

  return (
    <XRow sp={4} ref={refs}>
      {decks.map((deck, idx) => (
        <Deck
          key={deck.$meta.get("id", idx)}
          deck={deck}
          isSelected={deck.id === selectedDeckId}
          isOtherSelected={isSomeDeckSelected && deck.id !== selectedDeckId}
          ref={registerFlipNode(deck.$meta.get("id", idx), {
            parentFlipKey: `batch-${offset}`,
            onPresence: (val) => ({opacity: val}),
            positionSpringConfig: {noPointerEvents: true},
          })}
          root={root}
          history={history}
          canModifyOrder={canModifyOrder}
          dragInfo={{id: deck.id, sourceProjectId: project.id, sourceSpaceId: space.id}}
        />
      ))}
      {lastEl}
    </XRow>
  );
};

const DeckRoutesComp = lazy(
  () => import(/* webpackChunkName: "DeckRoutes" */ "./inspector/DeckRoutes")
);

export const DeckInspector = (props) => (
  <Suspense
    fallback={
      <div style={{height: 120, backgroundColor: xcolors.gray100}}>
        <Spinner />
      </div>
    }
  >
    <DeckRoutesComp {...props} />
  </Suspense>
);

const DeckInspectorLane = ({
  bag,
  selectedDeck,
  registerFlipNode,
  currentDeckIndex,
  scrollElementRef,
}) => {
  const {location, root, history, decksContainerLeft} = bag;
  const arrowLeft = decksContainerLeft + DECKWIDTHWOMARGIN / 2 + DECKWIDTH * currentDeckIndex;
  return (
    <div className={styles.deckInspectorBox.base}>
      {currentDeckIndex !== -1 && (
        <Arrow
          pointTo="top"
          size="md"
          color="white"
          ref={registerFlipNode("arrow", {onPresence: (val) => ({opacity: val})})}
          style={{left: arrowLeft}}
        />
      )}
      <DeckInspector
        location={location}
        deck={selectedDeck}
        root={root}
        history={history}
        scrollElementRef={scrollElementRef}
      />
    </div>
  );
};

export const useContainerPosition = (nodeRef) => {
  const [width, setWidth] = useState(null);
  const [left, setLeft] = useState(null);

  useLayoutEffect(() => {
    if (!nodeRef.current) return;
    const measure = () => {
      const newWidth = nodeRef.current.offsetWidth - 16 - 80 - 12;
      const newLeft = nodeRef.current.offsetLeft + 16 + 12;
      setWidth(newWidth);
      setLeft(newLeft);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(nodeRef.current);
    measure();
    return () => ro.disconnect();
  }, [nodeRef]);

  return {width, left};
};

const PublicProjectButton = ({project}) => (
  <TooltipForChild
    tooltip={`This project is publicly visible. ${
      project.commentsArePublic ? "Conversations are also public." : "Conversations are not public."
    } Click to open public view.`}
  >
    <DSIconButton
      size="md"
      variant="tertiary"
      href={`${cdxEnv.OPEN_APP_HOST}/${project.publicPath}`}
      target="_blank"
      rel="noopener noreferrer"
      icon={<DSIconGlobe />}
    />
  </TooltipForChild>
);

const ProjectDecks = ({
  selectedDeckInfo,
  selectedDeck,
  location,
  history,
  root,
  decksContainerWidth,
  decksContainerLeft,
  dragDeckInfo,
  setDragDeckInfo,
  onDeckDrop,
  scrollElementRef,
  spacesAndDecks,
  project,
  isDeckOrderLoaded,
  collapseSpaceInfo,
}) => {
  const loadStyles = useSpring({opacity: isDeckOrderLoaded ? 1 : 0});
  const canModifyOrder = hasPermissionToModifyDeck({root, project});

  const selDeckProjectId =
    (selectedDeck && selectedDeck.project.$meta.get("id")) ||
    (selectedDeckInfo && selectedDeckInfo.projectId);
  const selDeckId =
    (selectedDeck && selectedDeck.$meta.get("id")) || (selectedDeckInfo && selectedDeckInfo.deckId);

  const selectedDeckWithinProject = selDeckProjectId === project.id ? selectedDeck : null;

  return (
    <Col sp="16px" className={uiStyles.hideContainerSpaces} styleChild>
      <animated.div style={loadStyles}>
        <Row sp="12px" align="center">
          <ProjectHeader project={project} />
          {process.env.REACT_APP_MODE !== "open" && project.isPublic && (
            <PublicProjectButton project={project} />
          )}
          {canModifyOrder && (
            <Row ml="auto" sp="8px" className={uiStyles.hideElementSpaces}>
              <CreateSpace project={project} />
              <TooltipForChild tooltip="Open Project Settings">
                <DSIconButton
                  variant="secondary"
                  icon={<DSIconCog />}
                  size="md"
                  to={getModalUrl({
                    location,
                    modal: `projectSettings.${alphaSeq.intToSeq(project.accountSeq)}`,
                  })}
                />
              </TooltipForChild>
            </Row>
          )}
        </Row>
        <Col sp="12px">
          {spacesAndDecks.map(({space, decks}) => (
            <Space
              key={space.id}
              project={project}
              space={space}
              selectedDeckInfo={selectedDeckInfo}
              selectedDeck={selectedDeck}
              location={location}
              history={history}
              root={root}
              decksContainerWidth={decksContainerWidth}
              decksContainerLeft={decksContainerLeft}
              dragDeckInfo={dragDeckInfo}
              setDragDeckInfo={setDragDeckInfo}
              isDeckOrderLoaded={isDeckOrderLoaded}
              orderedDecks={isDeckOrderLoaded ? decks : []}
              onDeckDrop={onDeckDrop}
              scrollElementRef={scrollElementRef}
              canModifyOrder={canModifyOrder}
              collapseSpaceInfo={collapseSpaceInfo}
              selDeckId={selDeckId}
              selectedDeckWithinProject={selectedDeckWithinProject}
            />
          ))}
        </Col>
      </animated.div>
    </Col>
  );
};

const Space = ({
  project,
  space,
  location,
  history,
  root,
  selectedDeck,
  selDeckId,
  selectedDeckWithinProject,
  decksContainerWidth,
  decksContainerLeft,
  dragDeckInfo,
  setDragDeckInfo,
  orderedDecks,
  onDeckDrop,
  isDeckOrderLoaded,
  scrollElementRef,
  canModifyOrder,
  collapseSpaceInfo,
}) => {
  const decksPerRow = Math.max(1, Math.floor((decksContainerWidth + 24) / DECKWIDTH));
  const {isSpaceCollapsed, toggleSpaceCollapsed} = collapseSpaceInfo;
  const rawSpaceCollapsed = isSpaceCollapsed(project, space);

  const handleDragOver = ({position, item}, offset, batchDecks) => {
    if (!position || !canModifyOrder) {
      setDragDeckInfo(null);
    } else {
      const col = Math.max(0, Math.floor((position.x - 8) / DECKWIDTH));
      const row = Math.max(0, Math.floor((position.y + 8) / DECKHEIGHT));
      const maxIndex = batchDecks.some((d) => d.id === item.id)
        ? batchDecks.length - 1
        : batchDecks.length;
      const targetIndex =
        offset + Math.min(maxIndex, row * decksPerRow + Math.min(decksPerRow - 1, col));
      if (
        !dragDeckInfo ||
        dragDeckInfo.index !== targetIndex ||
        dragDeckInfo.overProjectId !== project.id
      ) {
        // only update state if it has changed
        setDragDeckInfo({
          deckId: item.id,
          index: targetIndex,
          overProjectId: project.id,
          overSpaceId: space.id,
          sourceProjectId: item.data.sourceProjectId,
          sourceSpaceId: item.data.sourceSpaceId,
        });
      }
    }
  };

  const bag = {
    setDragDeckInfo,
    canModifyOrder,
    onDeckDrop: ({item}) => {
      const {canDrop} = canDropFn({dragItem: item, isPublic: project.isPublic, canModifyOrder});
      if (canDrop) onDeckDrop(project.id, space.id);
    },
    handleDragOver,
    root,
    location,
    history,
    decksContainerWidth,
    decksContainerLeft,
    project,
    space,
  };

  const showSpace =
    !rawSpaceCollapsed ||
    (selectedDeckWithinProject && orderedDecks.some((d) => d.id === selDeckId));

  const handleCollapseClick = () => {
    if (!showSpace || !rawSpaceCollapsed) {
      toggleSpaceCollapsed(project, space);
    }

    if (selectedDeckWithinProject && orderedDecks.some((d) => d.id === selDeckId)) {
      history.replace(routes.decks.getUrl());
    }
  };

  return (
    <FlipGroup
      changeKey={`${selectedDeck ? selDeckId : "closed"}-${orderedDecks
        .map((d) => d.id)
        .join(",")}`}
      keysAndData={orderedDecks.map((deck) => ({
        key: deck.$meta.isLoaded ? deck.id.toString() : "none",
        data: deck,
      }))}
    >
      {(registerFlipNode, keysAndData) => {
        const hasNoDecks = keysAndData.length === 0;
        const getDecks = () => {
          if (!showSpace) return [];
          const allDecks = keysAndData.map((knd) => knd.data);
          if (!selectedDeck) allDecks.push("lastEl");
          return allDecks;
        };
        const decks = getDecks();
        const lanes = [];
        let i = 0;
        let offset = 0;
        while (decksPerRow && decks.length) {
          const lane = decks.splice(0, decksPerRow);
          let withLastEl = false;
          if (lane[lane.length - 1] === "lastEl") {
            withLastEl = true;
            lane.splice(lane.length - 1);
          }
          lanes.push(
            <DeckLane
              key={`lane-${(i += 1)}`}
              decks={lane}
              offset={offset}
              registerFlipNode={registerFlipNode}
              selectedDeckId={selDeckId}
              isSomeDeckSelected={!!selDeckId}
              bag={bag}
              lastEl={
                withLastEl &&
                hasPermissionToCreateDeck({root, project}) && (
                  <DecksCreatePlaceholder
                    type="deck"
                    location={location}
                    projectId={project.id}
                    spaceId={space.id}
                  />
                )
              }
            />
          );
          if (selectedDeckWithinProject && isDeckOrderLoaded) {
            const idx = lane.findIndex((d) => d.id === selDeckId);
            if (idx > -1) {
              const selDeck = selectedDeck.$meta.isLoaded ? selectedDeck : lane[idx];
              lanes.push(
                <DeckInspectorLane
                  key="inspector"
                  bag={bag}
                  registerFlipNode={registerFlipNode}
                  selectedDeck={selDeck}
                  currentDeckIndex={idx}
                  scrollElementRef={scrollElementRef}
                />
              );
            }
          }
          offset += lane.length;
        }
        return (
          <Col relative>
            <div className={cx(styles.spaceContainerBg.base, styles.spaceContainerBg.mode.close)} />
            <Row
              sp="24px"
              align="center"
              className={uiStyles.hideInnerContainer}
              pa="12px"
              cursor="pointer"
              userSelect="none"
              relative
              styleChild
            >
              <div onClick={handleCollapseClick} data-cdx-clickable>
                <TooltipForChild tooltip={showSpace ? "Hide decks" : "Show decks"} delayed>
                  <DSIconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCollapseClick();
                    }}
                    size="sm"
                    variant="tertiary"
                    icon={showSpace ? <DSIconChevronUp /> : <DSIconChevronDown />}
                  />
                </TooltipForChild>
                <Row sp="8px" align="center">
                  <SpaceIcon space={space} size={20} className={dsStyles.color.secondary} />
                  <Box size={14} color={showSpace ? "primary" : "secondary"}>
                    {space.name ?? "Default Space"}
                  </Box>
                </Row>
                {!showSpace && <SpaceStats decks={orderedDecks} />}
                {canModifyOrder && (
                  <Row sp="8px" align="center">
                    <Row
                      sp="12px"
                      className={uiStyles.hideInnerElement}
                      colorTheme="purpleTextOnDark"
                    >
                      <EditSpaceLabel project={project} space={space} isEmpty={hasNoDecks} />
                      {project.spaces.length > 1 && <EditOrderButton project={project} />}
                    </Row>
                  </Row>
                )}
              </div>
            </Row>
            {showSpace && (
              <Col sp="12px" px="12px" pb="12px'">
                {lanes}
              </Col>
            )}
          </Col>
        );
      }}
    </FlipGroup>
  );
};

const ShowSingleDeckLane = ({
  decksContainerLeft,
  project,
  selectedDeck,
  location,
  root,
  history,
  scrollElementRef,
}) => (
  <Col sp="24px" px="12px">
    <ProjectHeader project={project} />
    <XGrid sp={4}>
      <Deck deck={selectedDeck} isSelected root={root} history={history} />
    </XGrid>
    <div className={styles.deckInspectorBox.base}>
      <Arrow
        pointTo="top"
        size="md"
        color="white"
        style={{left: decksContainerLeft + 4 + DECKWIDTHWOMARGIN / 2}}
      />
      <DeckInspector
        location={location}
        deck={selectedDeck}
        root={root}
        history={history}
        scrollElementRef={scrollElementRef}
      />
    </div>
  </Col>
);

const useCollapsedSpaceInfo = () => {
  const [val, setVal] = useLocalStorageState("projects.collapsedSpaces", {});
  const toKey = (project, space) => `${project.id}:${space.id}`;

  return {
    isSpaceCollapsed: (project, space) => Boolean(val[toKey(project, space)]),
    toggleSpaceCollapsed: (project, space) => {
      const key = toKey(project, space);
      setVal({...val, [key]: !val[key]});
    },
  };
};

const ProjectGroups = ({
  location,
  projects,
  history,
  root,
  selectedDeck,
  selectedDeckSeq,
  scrollElementRef,
}) => {
  const [dragDeckInfo, setDragDeckInfo] = useState();
  const [inflightOrder, setInflightOrder] = useState(null);
  const collapseSpaceInfo = useCollapsedSpaceInfo();

  const decksContainerRef = useRef();
  const {width: decksContainerWidth, left: decksContainerLeft} =
    useContainerPosition(decksContainerRef);

  const deckSeqToDeckInfoRef = useRef({});
  projects.forEach((p) =>
    projectDecks(p).forEach((d) => {
      const seq = d.$meta.get("accountSeq", null);
      if (seq) deckSeqToDeckInfoRef.current[seq] = {deckId: d.id, projectId: p.id};
    })
  );

  const projectsLoaded = !(projects.length === 1 && !projects[0].$meta.isLoaded);
  const selectedDeckNotInProject =
    selectedDeck &&
    selectedDeck.$meta.isLoaded &&
    projectsLoaded &&
    (selectedDeck.isDeleted || !projects.some((p) => selectedDeck.project === p));

  const hasProjectWithManyDecks = projects.some((p) => projectDecks(p).length >= 8);

  const {isLoaded: isDeckOrderLoaded, result: currentSpacesAndDecksByProjectId} = useMemo(
    () =>
      checkIfPresent(
        () =>
          getDeckOrderWithSpaces({
            projects,
            dragInfo: dragDeckInfo,
          }),
        api
      ),
    [projects, dragDeckInfo]
  );

  const spacesAndDecksByProjectId = inflightOrder || currentSpacesAndDecksByProjectId;

  const handleDeckDrop = (targetProjectId, targetSpaceId) => {
    if (!dragDeckInfo) return;
    const {index, deckId} = dragDeckInfo;
    setInflightOrder(currentSpacesAndDecksByProjectId);
    canDeckBeMoved(dragDeckInfo.deckId, targetProjectId)
      .then((ok) => {
        setDragDeckInfo(null);
        if (ok) {
          const spacesAndDecks = spacesAndDecksByProjectId[targetProjectId];
          const targetSpaceAndDecks = spacesAndDecks.find(({space}) => space.id === targetSpaceId);
          if (!targetSpaceAndDecks) {
            console.warn("Found no target space for ", targetProjectId, targetSpaceId);
            return;
          }
          const {decks} = targetSpaceAndDecks;
          if (index === 0) {
            // `decks` is based on inflight order, so we need to exclude the element dragged to first row
            return api.mutate.decks.addToSpaceBefore({
              deckIds: [deckId],
              targetId: decks.find((d) => d.id !== deckId)?.$meta.get("id", null) ?? null,
              targetProjectId,
              targetSpaceId,
            });
          } else {
            const targetDeck = decks[Math.min(decks.length - 1, index - 1)];
            return api.mutate.decks.addToSpaceAfter({
              deckIds: [deckId],
              targetId: targetDeck ? targetDeck.$meta.get("id", null) : null,
              targetProjectId,
              targetSpaceId,
            });
          }
        }
      })
      .then(() =>
        waitForResultPromise(() =>
          getDeckOrderWithSpaces({
            projects: projects
              .map((p) => p.$meta.get("id", null))
              .filter(Boolean)
              .map((id) => api.getModel({modelName: "project", id})),
            dragInfo: null,
          })
        )
      )
      .then(
        () => setInflightOrder(null),
        (e) => {
          messenger.send(errorToString(e), {type: "error"});
          setInflightOrder(null);
        }
      );
  };

  return (
    <Col pl="16px" py="12px" sp="48px" pr="72px" flex="auto" minWidth="0" ref={decksContainerRef}>
      <RevealSpinner
        withCover
        show={Boolean(inflightOrder)}
        coverColor="hsl(227 24% 19% / 0.9)"
        fixed
        delay={400}
        coverMessage="Moving deck to new location..."
      />
      <WelcomeHints root={root}>
        {hasProjectWithManyDecks && (
          <HintForHintKey hintKey="decks.dndToOrder">
            <XCol sp={0}>
              <p>Did you know that you can re-order decks via drag and drop?</p>
            </XCol>
          </HintForHintKey>
        )}
      </WelcomeHints>

      {selectedDeckNotInProject && (
        <ShowSingleDeckLane
          project={selectedDeck.project}
          selectedDeck={selectedDeck}
          location={location}
          history={history}
          root={root}
          decksContainerLeft={decksContainerLeft}
          scrollElementRef={scrollElementRef}
        />
      )}
      {(decksContainerWidth ?? 0) > 0 &&
        projects.map((project, i) => (
          <ProjectDecks
            key={project.$meta.get("id", `un-${i}`)}
            project={project}
            selectedDeckInfo={selectedDeckSeq && deckSeqToDeckInfoRef.current[selectedDeckSeq]}
            selectedDeck={selectedDeck}
            location={location}
            history={history}
            root={root}
            decksContainerWidth={decksContainerWidth}
            decksContainerLeft={decksContainerLeft}
            dragDeckInfo={dragDeckInfo}
            setDragDeckInfo={setDragDeckInfo}
            onDeckDrop={handleDeckDrop}
            scrollElementRef={scrollElementRef}
            spacesAndDecks={spacesAndDecksByProjectId[project.id]}
            isDeckOrderLoaded={isDeckOrderLoaded}
            collapseSpaceInfo={collapseSpaceInfo}
          />
        ))}
    </Col>
  );
};

const DecksOverview = ({root, location, history, selectedDeckSeq}) => {
  const arenaCtx = getDecksArenaContext({history});
  const projects = useOrderedSelectedProjects(root);
  const scrollElementRef = useRef(null);

  const selectedDeck =
    selectedDeckSeq && root.account.$meta.find("anyDecks", {accountSeq: selectedDeckSeq})[0];

  const {getCardCount} = useFilteredCards(root, {}, {onlyCount: true});

  return (
    <ArenaContextProvider value={arenaCtx}>
      <ArenaLayout
        root={root}
        location={location}
        history={history}
        bg="gray550"
        activeTab="decks"
        searchElement={
          !selectedDeck && (
            <Search
              root={root}
              searchWithinLabel="your library"
              getCardCount={getCardCount}
              searchArenaName="library"
              basedOnFullLibrary
            />
          )
        }
        isDark
        showNotifications
        noSelectionHeader
        scrollNodeRef={scrollElementRef}
      >
        <DragController
          type="DECK"
          renderItem={({id}) => (
            <Deck
              deck={api.getModel({modelName: "deck", id})}
              isSelected
              root={root}
              history={history}
              isDragged
            />
          )}
        >
          <XRow fillParent pb={4} minWidth>
            {!selectedDeck ? (
              <OverviewActionButtons location={location} history={history} root={root} />
            ) : (
              <div style={{width: 36, flex: "none"}} />
            )}
            <ProjectGroups
              selectedDeck={selectedDeck}
              selectedDeckSeq={selectedDeckSeq}
              location={location}
              history={history}
              root={root}
              projects={projects}
              scrollElementRef={scrollElementRef}
            />
          </XRow>
        </DragController>
      </ArenaLayout>
    </ArenaContextProvider>
  );
};

export default DecksOverview;
